import React, { useCallback } from 'react';
import { useWindowSize } from 'react-use';
import { useParams } from 'react-router-dom';
import { routes, copyFor, config } from 'config';
import { SUB_NAV_HEIGHT, MOBILE_NAV_HEIGHT, MAX_NAV_WIDTH } from 'domain/style';
import { Grid, GridCell, Container } from 'styles';
import { usePageDisplay } from 'hooks/use-page-display.hook';
import { useAccountPlansCount } from 'hooks/use-account-plans-count.hook';
import { PlanSwitcher } from 'components/plan-switcher';
import { PlanSwitcherLite } from 'components/plan-switcher/plan-switcher-lite';
import { NavLinkTab } from '../nav-link-tab';
import { StyledNav } from './sub-nav.style';

const getCopy = copyFor('navigation.subNav');

const { breakpoints } = config.theme.media;

export const SubNav = () => {
  const { width } = useWindowSize();
  const { planId } = useParams<{ planId?: string }>();
  const desktopView = width > breakpoints.small;
  const { data } = usePageDisplay({ planId: Number(planId) });
  const plansCount = useAccountPlansCount();
  const { billing, solar, usage } = routes({ planId });
  const { displayBilling, displayUsage } = data;
  const linkPadding = desktopView ? '20px 8px' : '16px 8px';

  const getPlanSwitcher = useCallback(() => {
    if (plansCount.loading || plansCount.error) {
      return null;
    } else if (plansCount.data) {
      return plansCount.data.isPlanWhale ? (
        <PlanSwitcherLite selectedPlanId={Number(planId)} />
      ) : (
        <PlanSwitcher planId={Number(planId)} />
      );
    }
  }, [plansCount, planId]);

  return (
    <StyledNav>
      <Grid
        height={SUB_NAV_HEIGHT}
        small={{ height: MOBILE_NAV_HEIGHT }}
        maxWidth={MAX_NAV_WIDTH}
        margin="auto"
        alignItems="center"
      >
        <GridCell
          columnStart={2}
          columns={4}
          height="100%"
          small={{ columns: 5 }}
        >
          <Container
            flex
            height="100%"
            small={{ margin: { left: '30px' } }}
            alignItems="flex-end"
          >
            {displayBilling && (
              <NavLinkTab
                copy={getCopy('billing')}
                path={billing}
                margin={{ right: '40px' }}
                padding={linkPadding}
              />
            )}
            <NavLinkTab
              copy={getCopy('solar')}
              path={solar}
              margin={{ right: '40px' }}
              padding={linkPadding}
            />
            {displayUsage && (
              <NavLinkTab
                copy={getCopy('usage')}
                path={usage}
                padding={linkPadding}
              />
            )}
          </Container>
        </GridCell>
        {desktopView && (
          <GridCell height="100%" justifySelf="end" columnStart={8} columns={4}>
            <Container
              height="100%"
              flex
              alignItems="center"
              margin={{ top: '4px' }}
            >
              {getPlanSwitcher()}
            </Container>
          </GridCell>
        )}
      </Grid>
    </StyledNav>
  );
};
